import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { Advert, spotPageDetails } from 'interfaces/common';
import { AdvertService } from 'services/common';
import { FilterData, Pagination } from 'interfaces/core';
import { AdvertStatus } from 'enum/advert-status.enum';

// add more keys if needed
interface QueryParams {
  bodyTypes?: string | Array<string>;
  categories?: string | Array<string>;
  divisions?: string | Array<string>;
  hairColors?: string | Array<string>;
  intimateHairs?: string | Array<string>;
  orientations?: string | Array<string>;
  search?: string;
  types?: string | Array<string>;
}

type ComplexFilter<T extends string = string> = Array<
  Record<T, { slug: string }>
>;

@Component({
  selector: 'app-spot-list',
  templateUrl: './spot-list.component.html',
  styleUrls: ['./spot-list.component.scss'],
})
export class SpotListComponent implements OnInit, OnDestroy {
  @Input() pageData!: spotPageDetails;
  //Store data
  adverts: Array<Advert> = [];
  tagAdverts: Array<Advert> = [];
  searchQuery?: string;

  currentPage = 1;
  totalAdverts = 0;
  advertsPerPage = 12;

  isLoading = false;
  isLoadMore = false;

  // Complex Filter
  categoryFilter: ComplexFilter<'category'> = [];
  typeFilter: ComplexFilter<'type'> = [];
  divisionFilter: ComplexFilter<'division'> = [];
  intimateHairsFilter: ComplexFilter<'intimateHair'> = [];
  bodyTypesFilter: ComplexFilter<'bodyType'> = [];
  orientationsFilter: ComplexFilter<'orientation'> = [];
  hairColorsFilter: ComplexFilter<'hairColor'> = [];
  id!: string;
  type!: string;

  // Subscriptions
  private subRouteOne: Subscription = Subscription.EMPTY;
  private subAdvertDetails: Subscription = Subscription.EMPTY;

  constructor(
    private activatedRoute: ActivatedRoute,
    private advertService: AdvertService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subRouteOne = this.activatedRoute.params.subscribe(qParam => {
      const urlSegments = this.router.url.slice(1).split('-');
      this.type = urlSegments[0];
      this.id = urlSegments[1].split('?')[0];
      this.id = this.id === 'koebenhavn' ? 'København' : this.id;
      // Search Query
      this.searchQueryFromQueryParam(qParam);

      // Filter Query
      this.filterQueryFromQueryParam(qParam);

      // Fetch data
      this.getAllAdvertCombined();
    });
  }

  private searchQueryFromQueryParam(qParam: QueryParams) {
    this.searchQuery = qParam.search;
  }

  private filterQueryFromQueryParam(qParam: QueryParams) {
    if (qParam.categories) {
      this.categoryFilter = ['']
        .concat(qParam.categories)
        .map(p => ({ category: { slug: p } }));
    }

    if (qParam.types) {
      this.typeFilter = ['']
        .concat(qParam.types)
        .map(p => ({ type: { slug: p } }));
    }

    if (qParam.intimateHairs) {
      this.intimateHairsFilter = ['']
        .concat(qParam.intimateHairs)
        .map(p => ({ intimateHair: { slug: p } }));
    }

    if (qParam.bodyTypes) {
      this.bodyTypesFilter = ['']
        .concat(qParam.bodyTypes)
        .map(p => ({ bodyType: { slug: p } }));
    }

    if (qParam.orientations) {
      this.orientationsFilter = ['']
        .concat(qParam.orientations)
        .map(p => ({ orientation: { slug: p } }));
    }

    if (qParam.hairColors) {
      this.hairColorsFilter = ['']
        .concat(qParam.hairColors)
        .map(p => ({ hairColor: { slug: p } }));
    }

    if (qParam.divisions) {
      this.divisionFilter = ['']
        .concat(qParam.divisions)
        .map(p => ({ division: { slug: p } }));
    }
  }

  private getAllAdvertCombined(loadMore?: boolean) {
    const pagination: Pagination = {
      limit: Number(this.advertsPerPage),
      page: Number(this.currentPage) - 1,
    };

    // Compleax Filter Array Based on Selections
    const filters = [
      this.categoryFilter,
      this.typeFilter,
      this.divisionFilter,
      this.intimateHairsFilter,
      this.bodyTypesFilter,
      this.orientationsFilter,
      this.hairColorsFilter,
    ]
      .filter(f => f.length > 0)
      .map(f => ({ $or: f }));

    // TODO: add better type
    const filter: Record<string, unknown> = {
      ...(filters.length && { $or: filters }),
      type: { slug: this.type },
      status: AdvertStatus.Publish,
      isReviewed: true,
    };
    // Set dynamic properties

    filter[this.pageData?.spotType] = { slug: this.id.toLowerCase() };

    // Create the filterData object
    const filterData: FilterData = {
      pagination,
      filter,
      sort: [
        { bumpDate: 'desc' },
        { publishDate: 'desc' },
        { createdAt: 'desc' },
      ],
    };

    this.subAdvertDetails = this.advertService
      .getAllCombinedAdverts(filterData, this.searchQuery)
      .subscribe({
        next: res => {
          this.tagAdverts = res.data;
          this.isLoading = false;
          this.isLoadMore = false;
          this.adverts = loadMore
            ? [...this.adverts, ...this.tagAdverts]
            : [...this.tagAdverts];
          this.totalAdverts = res.count;
        },
        error: error => {
          this.isLoading = false;
          console.log(error);
        },
      });
  }

  ngOnDestroy(): void {
    this.subAdvertDetails.unsubscribe();
    this.subRouteOne.unsubscribe();
  }
}
